import React, { useEffect, useState, useCallback, useRef } from "react";
import "./searchableNodes.css";
import { ECLSearch, ECLThemeImage } from "../../../eclComponents";
import { translate } from "../../../utilities/Translate";
import { ReactFlow, useReactFlow, useOnViewportChange } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import CustomNode from "./components/CustomNode";
import OntologyNode from "./components/OntologyNode";

const nodeTypes = {
   customNode: CustomNode,
   ontologyNode: OntologyNode,
};
//Takes an array of nodes.  Nodes are an object with the following key/value pairs
// ID - unique identifier
// name - labels nodes
// type - determines styling of node/handling of preview
// searchAttrs - an object of arbitrary key pairs, once all nodes are read into component, the "search" functionality will use the
//      keys and values to determine appropriate input
//
//Also accepts and array of edges, which are a two column table, with IDs From and To
export default function SearchableNodes(props) {
   const [zoom, setZoom] = useState(0.1);
   const dontZoom = useRef(null);
   const lastSelection = useRef(null);
   const reactFlow = useReactFlow();

   useEffect(() => {
      reactFlow.zoomTo(zoom);
   }, [zoom]);

   useOnViewportChange({
      onEnd: (viewport) => {
         if (dontZoom.current) {
            dontZoom.current = false;
         } else {
            setZoom(viewport.zoom);
            dontZoom.current = true;
         }
      },
   });

   const updateSearchState = useCallback((values) => {
      values.forEach((v) => {
         if (v.value.filter.selections) {
            const newValues = {
               ...props.searchables,
               [v.key]: {
                  ...props.searchables[v.key],
                  selected: v.value.filter.selections.some(
                     (q) => q == v.searchableString,
                  ),
               },
            };
            const selections = newValues[v.key].selections
               .filter((s) => s.selected)
               .map((s) => s.searchableString);
            props.setNodes(
               props.nodes.map((node) =>
                  selections.includes(node.data.searchables[v.key])
                     ? { ...node, hidden: false }
                     : { ...node, hidden: true },
               ),
            );
         }
      });
   });

   return (
      <div className="cytoscape-container">
         <ECLSearch
            searchables={props.searchables}
            setSearchables={props.setSearchables}
            label={""}
            updateState={updateSearchState}
         />
         <ReactFlow
            nodes={props.nodes}
            edges={props.edges}
            style={{ height: "calc(100% - 50px)" }}
            minZoom={0.1}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            onSelectionChange={(e) => {
               if (e.nodes.length > 0) {
                  document.querySelector(
                     `[data-id="${e.nodes[0].id}"]`,
                  ).firstChild.className = "selected-node";
                  if (
                     lastSelection.current &&
                     lastSelection.current.id !== e.nodes[0].id
                  )
                     document.querySelector(
                        `[data-id="${lastSelection.current.id}"]`,
                     ).firstChild.className = "";
                  lastSelection.current = e.nodes[0];
                  props.setPreviewNode(e.nodes[0]);
               }
            }}
            fitView
         ></ReactFlow>
         <div className="cytoscape-control-widget">
            <button
               onClick={(e) => {
                  e.preventDefault();
                  setZoom((z) => (z - 0.25 > 0.1 ? z - 0.25 : 0.1));
               }}
            >
               <ECLThemeImage fontAwesome="minus" alt={translate("Zoom out")} />
            </button>
            <input
               type="range"
               value={zoom}
               onChange={(e) => {
                  setZoom(e.target.value);
               }}
               min={0.1}
               max={2}
               step={0.1}
            />
            <button
               onClick={(e) => {
                  e.preventDefault();
                  setZoom((z) => (z + 0.25 < 2 ? z + 0.25 : 2));
               }}
            >
               <ECLThemeImage fontAwesome="plus" alt={translate("Zoom in")} />
            </button>
            <button
               onClick={(e) => {
                  e.preventDefault();
                  reactFlow.fitView();
                  setZoom(0.1);
               }}
            >
               <ECLThemeImage fontAwesome="up-right-and-down-left-from-center" />
            </button>
         </div>
      </div>
   );
}
